// Lock state shapes per docs/ui/operator-ui-tds.md §5.2.
// The BFF (NEX-70) is authoritative; types here mirror its REST + WS payload
// shape so the central applyEvent merge (NEX-149) can write into the cache
// without an extra mapping layer.
//
// i18n: `LockConflictError` carries operator-facing contention copy (its
// `message`, and the `holderName` fallback that a dozen call sites interpolate
// into "PNR x is being edited by …"). It is not a component, so it resolves that
// copy through i18next at THROW time — bound to the namespace, never to a
// language, so it always speaks whatever language the console is showing.

import i18next from 'i18next';

const adminT = i18next.getFixedT(null, 'admin');

export interface LockHolder {
  operatorUrn: string;
  displayName: string;
}

export interface LockState {
  /** URN of the entity locked. Typically a sub-case (`urn:subcase:...`) or case (`urn:case:...`). */
  entityUrn: string;
  /** null when unlocked; holder details when locked. */
  holder: LockHolder | null;
  /** ISO timestamp when the BFF will release the lock unless heartbeated. Server clock authoritative. */
  expiresAt: string | null;
}

export interface AcquireLockResponse {
  state: LockState;
}

export class LockConflictError extends Error {
  readonly status = 409;
  readonly holder: LockHolder | null;
  readonly expiresAt: string | null;
  /** The PNR (sub-case) URN that's locked — surfaced in the contention message. */
  readonly entityUrn: string | null;

  constructor(state: LockState) {
    super(
      state.holder
        ? adminT('lock.conflict.lockedBy', { name: state.holder.displayName })
        : adminT('lock.conflict.generic'),
    );
    this.name = 'LockConflictError';
    this.holder = state.holder;
    this.expiresAt = state.expiresAt;
    this.entityUrn = state.entityUrn ?? null;
  }

  /** The holder's friendly name, or a safe fallback. */
  get holderName(): string {
    return this.holder?.displayName ?? i18next.t('common:holds.lock.anotherOperator');
  }
}

export function isLockConflictError(err: unknown): err is LockConflictError {
  return err instanceof LockConflictError;
}
